import { X, Sparkles, Check, Mail, Crown, Gem } from "lucide-react";
import { useState } from "react";

const SUPPORT_EMAIL = "help_mrscashback@outlook.com";

const TIERS = [
  {
    id: "free",
    name: "Free",
    price: null,
    boost: null,
    accent: "#64748B",
    Icon: Sparkles,
    perks: [
      "Standard cashback rate",
      "Unlimited receipt uploads",
      "Instant approval",
    ],
  },
  {
    id: "silver",
    name: "Silver",
    price: 3.75,
    boost: 5,
    accent: "#8FA3B8",
    Icon: Sparkles,
    perks: [
      "+5% on all cashback",
      "Priority receipt review",
      "Cancel anytime",
    ],
  },
  {
    id: "gold",
    name: "Gold",
    price: 7.50,
    boost: 9,
    accent: "#E0A020",
    Icon: Crown,
    popular: true,
    perks: [
      "+9% on all cashback",
      "Priority receipt review",
      "Early access to promos",
    ],
  },
  {
    id: "diamond",
    name: "Diamond",
    price: 10.50,
    boost: 12,
    accent: "#4FA9C7",
    Icon: Gem,
    perks: [
      "+12% on all cashback",
      "Priority receipt review",
      "Early access to promos",
      "VIP support line",
    ],
  },
];

export default function MembershipModal({ open, onClose }) {
  const [contactShown, setContactShown] = useState(null); // tier id when contact CTA is triggered

  if (!open) return null;

  const handlePick = (tier) => {
    if (tier.id === "free") return;
    setContactShown(tier.id);
  };

  const close = () => {
    setContactShown(null);
    onClose?.();
  };

  return (
    <div
      data-testid="membership-modal"
      onClick={close}
      style={{
        position: "fixed", inset: 0, zIndex: 260,
        background: "rgba(14, 34, 64, 0.82)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 18, backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%", maxWidth: 400, maxHeight: "90vh", overflowY: "auto",
          background: "var(--surface)", borderRadius: 28,
          padding: "22px 18px 18px", position: "relative",
          boxShadow: "0 30px 60px -24px rgba(0,0,0,0.45)",
        }}
      >
        <button
          onClick={close}
          data-testid="membership-close"
          aria-label="Close"
          style={{
            position: "absolute", top: 12, right: 12,
            width: 34, height: 34, borderRadius: 17,
            background: "var(--surface-soft)", border: "none",
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer",
          }}
        >
          <X size={18} color="var(--text-secondary)" />
        </button>

        <div style={{ textAlign: "center", marginBottom: 14 }}>
          <h2 style={{
            color: "var(--navy)", fontSize: 22, fontWeight: 800,
            letterSpacing: -0.4, margin: "0 0 6px",
          }}>
            Membership tiers
          </h2>
          <p style={{
            color: "var(--text-secondary)", fontSize: 13, lineHeight: 1.5,
            margin: "0 6px",
          }}>
            Boost your cashback rate with a monthly membership. Cancel anytime.
          </p>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {TIERS.map((t) => (
            <TierCard
              key={t.id}
              tier={t}
              onPick={() => handlePick(t)}
              expanded={contactShown === t.id}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function TierCard({ tier, onPick, expanded }) {
  const { name, price, boost, accent, Icon, perks, popular, id } = tier;
  const isFree = id === "free";

  return (
    <div
      data-testid={`membership-tier-${id}`}
      style={{
        position: "relative",
        background: "var(--surface-elev)",
        border: `1px solid ${popular ? accent : "var(--border)"}`,
        borderRadius: 18,
        padding: 14,
        boxShadow: popular ? `0 12px 24px -18px ${accent}` : "none",
      }}
    >
      {popular && (
        <span style={{
          position: "absolute", top: -9, right: 12,
          background: accent, color: "#FFFFFF",
          fontSize: 9, fontWeight: 800, letterSpacing: 0.8, textTransform: "uppercase",
          padding: "3px 8px", borderRadius: 999,
        }}>
          Most popular
        </span>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span style={{
          width: 34, height: 34, borderRadius: 10, flexShrink: 0,
          background: accent + "22", border: `1px solid ${accent}55`,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <Icon size={17} color={accent} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
            <span style={{ fontSize: 15, fontWeight: 800, color: "var(--navy)" }}>{name}</span>
            {boost != null && (
              <span style={{
                fontSize: 10, fontWeight: 800, letterSpacing: 0.4,
                color: accent, textTransform: "uppercase",
              }}>
                +{boost}% cashback
              </span>
            )}
          </div>
          <div style={{ fontSize: 12, color: "var(--text-secondary)", marginTop: 1 }}>
            {isFree ? "Included by default" : `£${price.toFixed(2)} / month`}
          </div>
        </div>
        {!isFree && (
          <button
            data-testid={`membership-pick-${id}`}
            onClick={onPick}
            style={{
              background: accent, color: "#FFFFFF",
              border: "none", borderRadius: 999,
              padding: "8px 14px", fontSize: 12, fontWeight: 800, letterSpacing: 0.4,
              cursor: "pointer",
              boxShadow: `0 8px 16px -10px ${accent}`,
            }}
          >
            {expanded ? "Contact" : "Upgrade"}
          </button>
        )}
      </div>

      <ul style={{
        listStyle: "none", padding: 0, margin: "10px 0 0",
        display: "grid", gap: 4,
      }}>
        {perks.map((p, i) => (
          <li key={i} style={{
            display: "flex", alignItems: "center", gap: 6,
            fontSize: 12, color: "var(--text-primary)", lineHeight: 1.4,
          }}>
            <Check size={12} color={accent} strokeWidth={3} />
            {p}
          </li>
        ))}
      </ul>

      {expanded && (
        <a
          data-testid={`membership-contact-${id}`}
          href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(`Upgrade to ${name} membership`)}&body=${encodeURIComponent(`Hi, I'd like to upgrade to the ${name} tier (£${price.toFixed(2)}/month, +${boost}% cashback).`)}`}
          style={{
            display: "flex", alignItems: "center", gap: 8,
            marginTop: 12, padding: "10px 12px",
            background: "var(--surface)", border: `1px solid ${accent}55`,
            borderRadius: 12, textDecoration: "none",
            color: "var(--text-primary)", fontSize: 12, lineHeight: 1.45,
          }}
        >
          <Mail size={14} color={accent} />
          <span>
            To activate <b>{name}</b>, email{" "}
            <b style={{ color: accent }}>{SUPPORT_EMAIL}</b>. We'll get you set up manually.
          </span>
        </a>
      )}
    </div>
  );
}
